import {
  type AbroadHome,
  type Club,
  type ClubSlot,
  type LessonItem,
  type ViewContext,
  addDays,
  canJoinLesson,
  canOpenClassBoard,
  clubsOnDay,
  isoWeekday,
  lessonFit,
  shortName,
  timeOf,
  todayIso,
  useAbroadHome,
  useClubMemberships,
  useClubs,
  useLessons,
  weekDays,
  weekStart,
} from '@shkola/api';
import { subjectLook, subjectShort } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { type CSSProperties, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DecorImage, Sticker } from '../design/kit';
import { placeOf, zoneShift } from './AbroadPage';
import { useMinute } from '../lib/now';

type StudentContext = Extract<ViewContext, { kind: 'student' }>;

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт'];
/** a break this long or longer is «велика перерва» */
const BIG_BREAK = 20;
/** a lesson takes two lines of «лінійка», a break one */
const RULE = 28;

const minutesOf = (iso: string) => {
  const [h, m] = timeOf(iso).split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

const monthTitle = (iso: string) =>
  new Intl.DateTimeFormat('uk-UA', { timeZone: 'UTC', month: 'long' }).format(
    new Date(`${iso}T00:00:00Z`),
  );

const dayTitle = (iso: string) => {
  const s = new Intl.DateTimeFormat('uk-UA', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${iso}T00:00:00Z`));
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/** «🌙 у вас ніч» for a lesson a student abroad learns from the materials */
const fitNote = (t: TFunction, fit: ReturnType<typeof lessonFit>) =>
  fit === 'live' ? '' : `${fit === 'night' ? '🌙' : '🏫'} ${t(`abroad.fit.${fit}`)}`;

const roomOf = (t: TFunction, room: string | null) =>
  room ? (/^\d/.test(room) ? t('schedule.room', { room }) : room) : '';

/** The break between two lessons: one line of the page, longer ones louder. */
function BreakLine({
  from,
  to,
  now,
  isToday,
}: {
  from: LessonItem;
  to: LessonItem;
  now: number;
  isToday: boolean;
}) {
  const { t } = useTranslation();
  const min = minutesOf(to.startsAt) - minutesOf(from.endsAt);
  if (min <= 0) return null;
  const current = isToday && Date.parse(from.endsAt) <= now && now < Date.parse(to.startsAt);
  const big = min >= BIG_BREAK;
  return (
    <div className="flex items-center pl-[76px]" style={{ height: RULE }}>
      <span
        className={`flex items-center gap-1.5 text-xs ${current ? 'font-semibold text-red-pen' : big ? 'font-semibold text-ink-soft' : 'text-faint'}`}
      >
        {big && <Sticker name="red-apple" size={16} />}
        {current
          ? t('student.schedule.breakNow', {
              min: Math.ceil((Date.parse(to.startsAt) - now) / 60_000),
            })
          : big
            ? t('student.schedule.bigBreakFor', { min })
            : t('student.schedule.break', { min })}
      </span>
    </div>
  );
}

/**
 * The chosen day on «лінійка», like a task list: the times in the margin, the lessons on the lines,
 * the breaks between them; the lesson going on now (or next) under the yellow highlighter with its
 * progress and the way in.
 */
/** After the lessons: the student's clubs of the day, on the same lines of the page. */
function ClubLines({ clubs }: { clubs: ReadonlyArray<{ club: Club; slot: ClubSlot }> }) {
  const { t } = useTranslation();
  return clubs.map(({ club, slot }) => (
    <div key={`${club.id}-${slot.startsAt}`} className="relative flex" style={{ height: RULE * 2 }}>
      <span className="flex w-[64px] shrink-0 flex-col items-end pt-2 pr-2.5">
        <span className="text-sm leading-5 font-bold text-ink">{slot.startsAt.slice(0, 5)}</span>
        <span className="text-[11px] leading-4 text-faint">{slot.endsAt.slice(0, 5)}</span>
      </span>
      <Link
        to="/clubs"
        className="mx-3 my-1 flex min-w-0 flex-1 items-center gap-3 rounded-[16px] px-2.5 hover:bg-fill"
      >
        <span className="w-9 text-center text-2xl">{club.emoji ?? '⭐'}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-base leading-5 font-bold text-ink">
            {club.title}
          </span>
          <span className="block truncate text-xs leading-4 text-muted">
            {[t('nav.clubs'), club.room].filter(Boolean).join(' · ')}
          </span>
        </span>
      </Link>
    </div>
  ));
}

function DayAgenda({
  date,
  lessons,
  clubs = [],
  now,
  home,
}: {
  date: string;
  lessons: LessonItem[];
  clubs?: ReadonlyArray<{ club: Club; slot: ClubSlot }>;
  now: number;
  /** a student abroad: times by the family's clock, and which lessons are not live for them */
  home?: AbroadHome | null;
}) {
  const { t } = useTranslation();
  const zone = home?.timezone;
  const isToday = date === todayIso();
  const held = lessons.filter((l) => l.status !== 'cancelled');
  const live = isToday
    ? held.find((l) => Date.parse(l.startsAt) <= now && now < Date.parse(l.endsAt))
    : undefined;
  const marked = live ?? (isToday ? held.find((l) => Date.parse(l.startsAt) > now) : undefined);
  const first = held[0];
  const last = held.at(-1);

  return (
    <div className="paper-lined relative pb-6">
      <span className="absolute inset-y-0 left-[64px] w-[1.5px] bg-paper-margin" />
      <div className="flex flex-col justify-end pr-5 pb-1.5 pl-[76px]" style={{ height: RULE * 2 }}>
        <p className="truncate font-display text-lg leading-6 font-bold text-ink">
          {dayTitle(date)}
        </p>
        {first && last && (
          <p className="text-xs leading-4 font-medium text-muted">
            {t('student.lessonsCount', { count: held.length })} · {timeOf(first.startsAt, zone)}–
            {timeOf(last.endsAt, zone)}
          </p>
        )}
      </div>
      {lessons.length === 0 && (
        <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
          <DecorImage name="sun" size={88} />
          <p className="font-display text-xl font-bold text-ink">{t('student.schedule.free')}</p>
          <p className="max-w-xs text-sm text-muted">{t('student.schedule.freeHint')}</p>
        </div>
      )}
      {lessons.map((l, i) => {
        const look = subjectLook(l.subject);
        const isLive = l === live;
        const mark = l === marked;
        const past = isToday && Date.parse(l.endsAt) <= now;
        const teacher = l.substitute ?? l.teachers[0];
        const progress = isLive
          ? (now - Date.parse(l.startsAt)) / (Date.parse(l.endsAt) - Date.parse(l.startsAt))
          : 0;
        const next = lessons[i + 1];
        return (
          <Fragment key={l.id}>
            <div
              className="motion-rise relative flex"
              style={
                {
                  height: RULE * (isLive ? 3 : 2),
                  opacity: past || l.status === 'cancelled' ? 0.45 : 1,
                  '--delay': `${i * 0.04}s`,
                } as CSSProperties
              }
            >
              <span className="flex w-[64px] shrink-0 flex-col items-end pt-2 pr-2.5">
                <span className="text-sm leading-5 font-bold text-ink">
                  {timeOf(l.startsAt, zone)}
                </span>
                <span className="text-[11px] leading-4 whitespace-nowrap text-faint">
                  {home ? t('abroad.kyiv', { time: timeOf(l.startsAt) }) : timeOf(l.endsAt)}
                </span>
              </span>
              <div
                className={`mx-3 my-1 flex min-w-0 flex-1 flex-col justify-center rounded-[16px] px-2.5 ${mark ? 'bg-lemon' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <Sticker name={look.sticker} size={36} rotate={-6} />
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block truncate font-display text-base leading-5 font-bold text-ink ${l.status === 'cancelled' ? 'line-through' : ''}`}
                    >
                      {l.subject}
                    </span>
                    <span className="block truncate text-xs leading-4 text-muted">
                      {[
                        home && fitNote(t, lessonFit(l, home)),
                        roomOf(t, l.room),
                        teacher && shortName(teacher),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  {canJoinLesson(l, todayIso()) ? (
                    <Link
                      to="/lesson/$lessonId"
                      params={{ lessonId: l.id }}
                      className="shrink-0 rounded-full bg-ink px-3.5 py-1.5 text-xs font-bold text-white"
                    >
                      {t('student.lessons.join')}
                    </Link>
                  ) : canOpenClassBoard(l, todayIso()) ? (
                    <Link
                      to="/class-board/$lessonId"
                      params={{ lessonId: l.id }}
                      className="shrink-0 rounded-full bg-ink px-3.5 py-1.5 text-xs font-bold text-white"
                    >
                      {t('classBoard.open')}
                    </Link>
                  ) : (
                    mark && (
                      <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-ink">
                        {isLive
                          ? t('student.schedule.left', {
                              min: Math.ceil((Date.parse(l.endsAt) - now) / 60_000),
                            })
                          : t('student.lessons.next')}
                      </span>
                    )
                  )}
                </div>
                {isLive && (
                  <span className="mt-2 ml-12 block h-1.5 overflow-hidden rounded-full bg-white">
                    <span
                      className="block h-full rounded-full bg-ink"
                      style={{ width: `${Math.round(progress * 100)}%` }}
                    />
                  </span>
                )}
              </div>
            </div>
            {next && <BreakLine from={l} to={next} now={now} isToday={isToday} />}
          </Fragment>
        );
      })}
      <ClubLines clubs={clubs} />
    </div>
  );
}

/**
 * The week as a calendar board: a row per lesson number with its time written once, a column per
 * day; today dark at the top, the lesson going on now on yellow, the big break as a line across
 * the week. A tap on a day opens it in the list on the left.
 */
function WeekBoard({
  days,
  lessons,
  selected,
  onSelect,
  now,
  zone,
}: {
  days: string[];
  lessons: LessonItem[];
  selected: string;
  onSelect: (day: string) => void;
  now: number;
  /** a student abroad: the times by the family's clock */
  zone?: string;
}) {
  const { t } = useTranslation();
  const today = todayIso();
  const periods = [...new Set(lessons.map((l) => l.period))].sort((a, b) => a - b);
  const timeOfPeriod = (p: number) => {
    const ls = lessons.filter((l) => l.period === p);
    const start = Math.min(...ls.map((l) => minutesOf(l.startsAt)));
    const end = Math.max(...ls.map((l) => minutesOf(l.endsAt)));
    return { start, end };
  };
  const hhmm = (m: number) =>
    `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

  return (
    <section className="rounded-[28px] bg-white p-3 xl:col-span-8">
      <div className="grid grid-cols-[64px_repeat(5,minmax(0,1fr))] gap-1.5">
        <span />
        {days.map((d, i) => (
          <button
            key={d}
            type="button"
            aria-pressed={d === selected}
            onClick={() => onSelect(d)}
            className={`flex items-center justify-center gap-2 rounded-[16px] py-2.5 transition-colors ${d === today ? 'bg-ink text-white' : d === selected ? 'bg-fill text-ink' : 'text-ink hover:bg-fill'}`}
          >
            <span className="text-sm font-medium opacity-60">{WEEKDAYS[i]}</span>
            <span className="font-display text-xl font-bold">{Number(d.slice(8, 10))}</span>
          </button>
        ))}
        {periods.map((p, row) => {
          const { start, end } = timeOfPeriod(p);
          const nextP = periods[row + 1];
          const gap = nextP !== undefined ? timeOfPeriod(nextP).start - end : 0;
          return (
            <Fragment key={p}>
              <span className="flex flex-col items-end justify-center pr-2">
                <span className="font-display text-base leading-5 font-bold text-ink">{p}</span>
                <span className="text-[11px] leading-4 text-muted">
                  {zone
                    ? timeOf(lessons.find((l) => l.period === p)?.startsAt ?? '', zone)
                    : hhmm(start)}
                </span>
              </span>
              {days.map((d) => {
                const l = lessons.find((x) => x.date === d && x.period === p);
                if (!l) return <span key={d} />;
                const look = subjectLook(l.subject);
                const isLive = Date.parse(l.startsAt) <= now && now < Date.parse(l.endsAt);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => onSelect(d)}
                    title={[
                      l.subject,
                      `${timeOf(l.startsAt, zone)}–${timeOf(l.endsAt, zone)}`,
                      roomOf(t, l.room),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    className={`group relative min-h-[62px] rounded-[14px] py-2 pr-8 pl-3 text-left transition-transform duration-500 ease-spring hover:-translate-y-0.5 ${isLive ? 'bg-lemon' : 'bg-fill'} ${l.status === 'cancelled' ? 'opacity-45' : ''}`}
                  >
                    <span
                      className={`line-clamp-2 font-display text-[14px] leading-[18px] font-bold text-ink ${l.status === 'cancelled' ? 'line-through' : ''}`}
                    >
                      {subjectShort(l.subject)}
                    </span>
                    <span className="block truncate text-[11px] text-muted">
                      {roomOf(t, l.room)}
                    </span>
                    {l.format !== 'in_person' && (
                      <span className="block text-[11px] font-semibold text-live">
                        {t(`format.${l.format}`)}
                      </span>
                    )}
                    <Sticker
                      name={look.sticker}
                      size={24}
                      rotate={8}
                      className="absolute top-2 right-2 transition-transform duration-500 ease-spring group-hover:scale-110"
                    />
                  </button>
                );
              })}
              {gap >= BIG_BREAK && (
                <span className="col-span-6 flex items-center gap-2 py-0.5 pl-[70px] text-xs font-semibold text-ink-soft">
                  <Sticker name="red-apple" size={16} />
                  {t('student.schedule.bigBreakFor', { min: gap })}
                  <span className="h-px flex-1 border-t border-dashed border-faint/60" />
                </span>
              )}
            </Fragment>
          );
        })}
      </div>
    </section>
  );
}

const MONTH_DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];

const monthLabel = (iso: string) => {
  const s = new Intl.DateTimeFormat('uk-UA', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${iso}T00:00:00Z`));
  return s.charAt(0).toUpperCase() + s.slice(1).replace(' р.', '');
};

/**
 * The month as a small calendar: the chosen day in a black circle, its week on grey, today with a
 * red dot; school days are tappable, weekends quiet. Arrows turn the month.
 */
function MonthCalendar({ selected, onPick }: { selected: string; onPick: (day: string) => void }) {
  const { t } = useTranslation();
  const today = todayIso();
  const [month, setMonth] = useState(() => `${selected.slice(0, 7)}-01`);
  const first = weekStart(month);
  const turn = (delta: number) => {
    const d = new Date(`${month}T00:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() + delta);
    setMonth(d.toISOString().slice(0, 10));
  };
  const weeks = Array.from({ length: 6 }, (_, w) => addDays(first, w * 7)).filter(
    (monday, w) => w < 5 || monday.slice(0, 7) === month.slice(0, 7),
  );
  const selectedWeek = weekStart(selected);
  return (
    <section className="rounded-[28px] bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <p className="font-display text-lg font-bold text-ink">{monthLabel(month)}</p>
        <div className="flex gap-1">
          <button
            type="button"
            aria-label={t('schedule.prevWeek')}
            onClick={() => turn(-1)}
            className="flex size-9 items-center justify-center rounded-full bg-fill text-lg text-ink transition hover:bg-line"
          >
            ‹
          </button>
          <button
            type="button"
            aria-label={t('schedule.nextWeek')}
            onClick={() => turn(1)}
            className="flex size-9 items-center justify-center rounded-full bg-fill text-lg text-ink transition hover:bg-line"
          >
            ›
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 text-center">
        {MONTH_DAYS.map((d, i) => (
          <span
            key={d}
            className={`pb-2 text-xs font-semibold ${i > 4 ? 'text-faint' : 'text-muted'}`}
          >
            {d}
          </span>
        ))}
      </div>
      <div className="space-y-1">
        {weeks.map((monday) => (
          <div
            key={monday}
            className={`grid grid-cols-7 rounded-full ${monday === selectedWeek ? 'bg-fill' : ''}`}
          >
            {Array.from({ length: 7 }, (_, i) => addDays(monday, i)).map((d, i) => {
              const outside = d.slice(0, 7) !== month.slice(0, 7);
              const weekend = i > 4;
              const isSelected = d === selected;
              return (
                <button
                  key={d}
                  type="button"
                  disabled={weekend}
                  onClick={() => onPick(d)}
                  className={`relative mx-auto flex size-10 items-center justify-center rounded-full text-[15px] font-semibold transition ${isSelected ? 'bg-ink text-white' : weekend || outside ? 'text-faint' : 'text-ink hover:bg-white'}`}
                >
                  {Number(d.slice(8, 10))}
                  {d === today && !isSelected && (
                    <span className="absolute bottom-1 size-1.5 rounded-full bg-red-pen" />
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * The student's week like a task manager: the chosen day as a list on «лінійка» with the lesson
 * going on now and the breaks, and the whole week as a board on «клітинка».
 */
export function StudentSchedule({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const now = useMinute();
  const today = todayIso();
  const weekend = isoWeekday(today) >= 6;
  const [monday, setMonday] = useState(() =>
    weekend ? addDays(weekStart(today), 7) : weekStart(today),
  );
  const days = weekDays(monday).slice(0, 5);
  const [picked, setPicked] = useState<string>();
  const pick = (day: string) => {
    setMonday(weekStart(day));
    setPicked(day);
  };
  const selected =
    picked && days.includes(picked) ? picked : days.includes(today) ? today : (days[0] as string);
  const lessons = useLessons(
    ctx.schoolId,
    { kind: 'student', studentId: ctx.child.memberId },
    days[0] as string,
    days[4] as string,
  );
  const all = lessons.data ?? [];
  const clubs = useClubs(ctx.schoolId).data ?? [];
  const memberships = useClubMemberships([ctx.child.memberId]).data ?? [];
  const dayClubs = clubsOnDay(clubs, memberships, ctx.child.memberId, isoWeekday(selected));
  const home = useAbroadHome(ctx.child.memberId).data;

  return (
    <div className="space-y-2">
      <header className="flex flex-wrap items-end justify-between gap-4 rounded-[28px] bg-white px-7 py-6">
        <div>
          <p className="-rotate-2 font-hand text-2xl font-bold text-ink-soft">
            {monthTitle(monday)}
          </p>
          <h1 className="font-display text-5xl font-bold tracking-tight text-ink">
            {t('schedule.title')}
          </h1>
          {home && (
            <p className="mt-2 text-sm text-muted">
              {placeOf(home)} · {zoneShift(t, home.timezone)}
            </p>
          )}
        </div>
        {monday !== weekStart(today) && !weekend && (
          <button
            type="button"
            onClick={() => pick(today)}
            className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white"
          >
            {t('schedule.thisWeek')}
          </button>
        )}
      </header>

      <div className="grid items-start gap-2 xl:grid-cols-12">
        <div className="space-y-2 xl:col-span-4">
          <MonthCalendar key={selected} selected={selected} onPick={pick} />
          <section className="overflow-hidden rounded-[28px]">
            <DayAgenda
              date={selected}
              lessons={all.filter((l) => l.date === selected)}
              clubs={dayClubs}
              now={now}
              home={home}
            />
          </section>
        </div>
        {all.length === 0 && !lessons.isPending ? (
          <section className="flex flex-col items-center gap-2 rounded-[28px] bg-white p-16 text-center xl:col-span-8">
            <DecorImage name="sun" size={96} />
            <p className="font-display text-2xl font-bold text-ink">{t('student.schedule.free')}</p>
          </section>
        ) : (
          <WeekBoard
            days={days}
            lessons={all}
            selected={selected}
            onSelect={pick}
            now={now}
            {...(home && { zone: home.timezone })}
          />
        )}
      </div>
    </div>
  );
}
